import React, { useEffect, useState } from "react";
import { Accordion } from "react-bootstrap";
import { FaEye, FaEyeSlash, FaArrowUp, FaArrowDown } from "react-icons/fa";
import "./LayersPanel.css";
import LayerExport from "./LayerExport";
import LayerGroupsManager from "./LayerGroupsManager";
import WmsLegend from "./WmsLegend/WmsLegend";
import LasLayerControls from "./lasControls/LasLayerControls";
import { showMapConfirmToast } from "../../../utils/Toast/mapToast";

const LAYER_LABEL_OVERRIDES = {
    trafo_stanice: "Trafostanice",
    trafo_stanica: "Trafostanica",

    mjerna_mjesta: "Mjerna mjesta",
    mjerno_mjesto: "Mjerno mjesto",

    ocitacki_hod: "Očitački hod",
    ocitacki_hodovi: "Očitački hodovi",

    stubovi: "Stubovi",
    stub: "Stub",

    vod: "Dalekovodi",
    vodovi: "Dalekovodi",
    dalekovod: "Dalekovod",
    dalekovodi: "Dalekovodi",
};

const toTitleCase = (text = "") =>
    text
        .toLowerCase()
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const formatLayerDisplayName = (layer = {}) => {
    const raw =
        layer.name ||
        layer.title ||
        layer.props?.title ||
        layer.props?.name ||
        layer.props?.layerName ||
        layer.id ||
        "";

    const rawName = String(raw)
        .replace(/^wfs[-_:]/i, "")
        .replace(/^wms[-_:]/i, "")
        .replace(/^pgsql-layer-/i, "")
        .replace(/^pgsql[-_:]/i, "")
        .replace(/^elektro:/i, "")
        .replace(/^GeoSolution:/i, "")
        .replace(/^[^:]+:/, "")
        .replace(/-layer$/i, "")
        .replace(/-\d{10,}$/g, "")
        .trim();

    if (!rawName) return "Neimenovani sloj";

    const normalizedKey = rawName
        .replace(/-/g, "_")
        .replace(/\s+/g, "_")
        .toLowerCase();

    if (LAYER_LABEL_OVERRIDES[normalizedKey]) {
        return LAYER_LABEL_OVERRIDES[normalizedKey];
    }

    return toTitleCase(rawName.replace(/[_-]+/g, " "));
};

const groupLayersByIdAndSource = (layers) => {
    const groups = {
        kml: [],
        gml: [],
        geojson: [],
        las: [],
        pgsql: [],
        wfs: [],
        dxf: [],
    };

    for (const layer of layers) {
        const id = layer.id?.toLowerCase() || "";
        const source = layer.props?.source;

        if (id.startsWith("kml")) groups.kml.push(layer);
        else if (id.startsWith("gml")) groups.gml.push(layer);
        else if (id.startsWith("geojson")) groups.geojson.push(layer);
        else if (id.startsWith("las")) groups.las.push(layer);
        else if (id.startsWith("pgsql")) groups.pgsql.push(layer);
        else if (id.startsWith("dxf") || id.startsWith("dx")) groups.dxf.push(layer);
        else if (source === "internal" || source === "catalog" || id.startsWith("wfs")) {
            groups.wfs.push(layer);
        }
    }

    return Object.fromEntries(
        Object.entries(groups).filter(([_, arr]) => arr.length > 0)
    );
};

const getUngroupedLayers = (layers, groups) => {
    const groupedIds = groups.flatMap((g) => g.layerIds);
    return layers.filter((layer) => !groupedIds.includes(layer.id));
};

const LayerPanel = ({
    wfsLayers = [],
    wmsLayers = [],
    lasLayers = [],
    setWfsLayers,
    setWmsLayers,
    setLasLayers,
}) => {
    const [groups, setGroups] = useState(() => {
        const saved = localStorage.getItem("layerGroups");

        try {
            const parsed = saved ? JSON.parse(saved) : [];
            return parsed.map((g) => ({
                ...g,
                layerIds: Array.isArray(g.layerIds) ? g.layerIds : [],
            }));
        } catch {
            return [];
        }
    });

    const ungroupedWfs = getUngroupedLayers(wfsLayers, groups);
    const ungroupedWms = getUngroupedLayers(wmsLayers, groups);
    const ungroupedLas = getUngroupedLayers(lasLayers, groups);

    const groupedWfsLayers = groupLayersByIdAndSource(ungroupedWfs);
    const groupedLasLayers = groupLayersByIdAndSource(ungroupedLas);

    const showWms = ungroupedWms.length > 0;

    useEffect(() => {
        localStorage.setItem("layerGroups", JSON.stringify(groups));
    }, [groups]);

    const showLayerHiddenToast = (layer) => {
        showMapConfirmToast({
            title: "Sloj je sakriven",
            message: `Sloj "${formatLayerDisplayName(layer)}" je postao potpuno nevidljiv i biće sakriven.`,
            confirmText: "",
            cancelText: "",
            variant: "warning",
            autoClose: 3000,
            showTimer: true,
        });
    };

    const showLayerDeletedToast = (layer) => {
        showMapConfirmToast({
            title: "Sloj obrisan",
            message: `Sloj "${formatLayerDisplayName(layer)}" je obrisan.`,
            confirmText: "",
            cancelText: "",
            variant: "info",
            autoClose: 3000,
            showTimer: true,
        });
    };

    const toggleVisibility = (layer, index, type, isWms = false, isLas = false) => {
        const updatedLayer = layer.clone({
            visible: !layer.props.visible,
        });

        if (isWms) {
            const updated = [...wmsLayers];
            updated[index] = updatedLayer;
            setWmsLayers(updated);
            return;
        }

        if (isLas) {
            const updated = [...lasLayers];
            updated[index] = updatedLayer;
            setLasLayers(updated);
            return;
        }

        const allGrouped = groupLayersByIdAndSource(wfsLayers);

        if (!allGrouped[type]) return;

        allGrouped[type][index] = updatedLayer;
        const merged = Object.values(allGrouped).flat();

        setWfsLayers(merged);
    };

    const changeOpacity = (layer, value, index, type, isWms = false, isLas = false) => {
        const newOpacity = parseFloat(value);
        const shouldHide = newOpacity === 0;

        if (shouldHide) {
            showLayerHiddenToast(layer);
        }

        const updatedLayer = layer.clone({
            opacity: newOpacity,
            visible: shouldHide ? false : true,
        });

        if (isWms) {
            const updated = [...wmsLayers];
            updated[index] = updatedLayer;
            setWmsLayers(updated);
            return;
        }

        if (isLas) {
            const updated = [...lasLayers];
            updated[index] = updatedLayer;
            setLasLayers(updated);
            return;
        }

        const allGrouped = groupLayersByIdAndSource(wfsLayers);

        if (!allGrouped[type]) return;

        allGrouped[type][index] = updatedLayer;
        const merged = Object.values(allGrouped).flat();

        setWfsLayers(merged);
    };

    const getLayerDisplayDeleteKey = (layer = {}) =>
        formatLayerDisplayName(layer)
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim();

    const deleteLayer = (layer, index, type, isWms = false, isLas = false) => {
        if (isLas) {
            const updated = [...lasLayers];
            updated.splice(index, 1);
            setLasLayers(updated);
            showLayerDeletedToast(layer);
            return;
        }

        const deleteKey = getLayerDisplayDeleteKey(layer);

        const relatedLayerIds = [...wfsLayers, ...wmsLayers]
            .filter((l) => getLayerDisplayDeleteKey(l) === deleteKey)
            .map((l) => l.id);

        setWfsLayers((prev) =>
            prev.filter((l) => getLayerDisplayDeleteKey(l) !== deleteKey)
        );

        setWmsLayers((prev) =>
            prev.filter((l) => getLayerDisplayDeleteKey(l) !== deleteKey)
        );

        setGroups((prev) =>
            prev.map((group) => ({
                ...group,
                layerIds: group.layerIds.filter((id) => !relatedLayerIds.includes(id)),
            }))
        );

        showLayerDeletedToast(layer);
    };

    const moveLayer = (index, direction, type) => {
        const grouped = groupLayersByIdAndSource(wfsLayers);
        const layerGroup = grouped[type];

        if (!layerGroup || index < 0 || index >= layerGroup.length) return;

        const newIndex = index + direction;

        if (newIndex < 0 || newIndex >= layerGroup.length) return;

        [layerGroup[index], layerGroup[newIndex]] = [
            layerGroup[newIndex],
            layerGroup[index],
        ];

        const merged = Object.values(grouped).flat();

        setWfsLayers(merged);
    };

    const moveWmsLayer = (index, direction) => {
        const newIndex = index + direction;

        if (newIndex < 0 || newIndex >= wmsLayers.length) return;

        const updated = [...wmsLayers];

        [updated[index], updated[newIndex]] = [
            updated[newIndex],
            updated[index],
        ];

        setWmsLayers(updated);
    };

    const assignLayerToGroup = (layerId, groupName) => {
        setGroups((prev) =>
            prev.map((group) =>
                group.name === groupName
                    ? {
                        ...group,
                        layerIds: [...new Set([...group.layerIds, layerId])],
                    }
                    : group
            )
        );
    };

    const removeLayerFromGroups = (layerId) => {
        setGroups((prev) =>
            prev.map((group) => ({
                ...group,
                layerIds: group.layerIds.filter((id) => id !== layerId),
            }))
        );
    };

    const getGroupNameByLayerId = (layerId) => {
        const group = groups.find(
            (g) => Array.isArray(g.layerIds) && g.layerIds.includes(layerId)
        );

        return group?.name || null;
    };

    const renderGroupControl = (layer, type) => (
        <div style={{ marginTop: "8px" }}>
            <label style={{ display: "block", marginBottom: "4px" }}>Grupa:</label>

            {getGroupNameByLayerId(layer.id) ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <span>{getGroupNameByLayerId(layer.id)}</span>

                    <button
                        onClick={() => removeLayerFromGroups(layer.id)}
                        style={{
                            backgroundColor: "#ffc107",
                            color: "#000",
                            border: "none",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }}
                        title="Ukloni sloj iz grupe"
                    >
                        Izađi
                    </button>
                </div>
            ) : (
                <select
                    onChange={(e) => {
                        if (e.target.value) {
                            assignLayerToGroup(layer.id, e.target.value, type);
                        }
                    }}
                    defaultValue=""
                    style={{
                        width: "100%",
                        padding: "6px",
                        borderRadius: "4px",
                    }}
                >
                    <option value="">-- Dodaj u grupu --</option>

                    {groups.map((group) => (
                        <option key={group.name} value={group.name}>
                            {group.name}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );

    const renderOrderControls = ({ index, lastIndex, onMoveUp, onMoveDown }) => (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
            }}
        >
            <span>Redoslijed:</span>

            <div style={{ display: "flex", gap: "8px" }}>
                <button
                    onClick={onMoveUp}
                    disabled={index === 0}
                    title="Pomjeri sloj gore"
                    style={{
                        background: "none",
                        border: "none",
                        cursor: index === 0 ? "not-allowed" : "pointer",
                        color: index === 0 ? "#888" : "#fff",
                    }}
                >
                    <FaArrowUp />
                </button>

                <button
                    onClick={onMoveDown}
                    disabled={index === lastIndex}
                    title="Pomjeri sloj dole"
                    style={{
                        background: "none",
                        border: "none",
                        cursor: index === lastIndex ? "not-allowed" : "pointer",
                        color: index === lastIndex ? "#888" : "#fff",
                    }}
                >
                    <FaArrowDown />
                </button>
            </div>
        </div>
    );

    const renderVisibilityControl = ({ layer, onToggle }) => (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                color: "white",
            }}
        >
            <span>Vidljivost:</span>

            <button
                onClick={onToggle}
                style={{
                    border: "none",
                    background: "none",
                    color: "white",
                }}
                title={layer.props.visible ? "Sakrij sloj" : "Prikaži sloj"}
            >
                {layer.props.visible ? <FaEye /> : <FaEyeSlash />}
            </button>
        </div>
    );

    const renderOpacityControl = ({ layer, onChange }) => (
        <div style={{ marginTop: "8px" }}>
            <label style={{ display: "block", marginBottom: "4px" }}>
                Prozirnost: {Math.round(parseFloat(layer.props.opacity ?? 1) * 100)}%
            </label>

            <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={layer.props.opacity ?? 1}
                onChange={onChange}
                style={{ width: "100%" }}
            />
        </div>
    );

    const renderDeleteButton = ({ onDelete }) => (
        <div style={{ marginTop: "10px" }}>
            <button
                onClick={onDelete}
                style={{
                    width: "100%",
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    cursor: "pointer",
                }}
            >
                Obriši sloj
            </button>
        </div>
    );

    return (
        <div className="addedlayers-panel" style={{ padding: "10px" }}>
            {Object.entries(groupedWfsLayers).map(([type, layers]) => (
                <div key={type}>
                    <h6 style={{ marginTop: "10px", fontWeight: "bold" }}>
                        {type.toUpperCase()} slojevi
                    </h6>

                    <Accordion alwaysOpen>
                        {layers.map((layer, index) => (
                            <Accordion.Item
                                eventKey={`${type}-${index}`}
                                key={layer.id || index}
                            >
                                <Accordion.Header>
                                    {formatLayerDisplayName(layer)}
                                </Accordion.Header>

                                <Accordion.Body>
                                    {renderOrderControls({
                                        index,
                                        lastIndex: layers.length - 1,
                                        onMoveUp: () => moveLayer(index, -1, type),
                                        onMoveDown: () => moveLayer(index, 1, type),
                                    })}

                                    {renderVisibilityControl({
                                        layer,
                                        onToggle: () => toggleVisibility(layer, index, type),
                                    })}

                                    {type === "wfs" && <LayerExport layer={layer} />}

                                    {renderGroupControl(layer, type)}

                                    {renderOpacityControl({
                                        layer,
                                        onChange: (e) =>
                                            changeOpacity(layer, e.target.value, index, type),
                                    })}

                                    {renderDeleteButton({
                                        onDelete: () => deleteLayer(layer, index, type),
                                    })}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </div>
            ))}

            {showWms && (
                <div>
                    <h6 style={{ marginTop: "10px", fontWeight: "bold" }}>
                        WMS slojevi
                    </h6>

                    <Accordion alwaysOpen>
                        {ungroupedWms.map((layer, index) => (
                            <Accordion.Item
                                eventKey={`wms-${index}`}
                                key={layer.id || index}
                            >
                                <Accordion.Header>
                                    {formatLayerDisplayName(layer)}
                                </Accordion.Header>

                                <Accordion.Body>
                                    {renderOrderControls({
                                        index,
                                        lastIndex: ungroupedWms.length - 1,
                                        onMoveUp: () => moveWmsLayer(index, -1),
                                        onMoveDown: () => moveWmsLayer(index, 1),
                                    })}

                                    {renderVisibilityControl({
                                        layer,
                                        onToggle: () =>
                                            toggleVisibility(layer, index, "wms", true),
                                    })}

                                    {renderGroupControl(layer, "wms")}

                                    <WmsLegend layer={layer} />

                                    {renderOpacityControl({
                                        layer,
                                        onChange: (e) =>
                                            changeOpacity(
                                                layer,
                                                e.target.value,
                                                index,
                                                "wms",
                                                true
                                            ),
                                    })}

                                    {renderDeleteButton({
                                        onDelete: () =>
                                            deleteLayer(layer, index, "wms", true),
                                    })}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </div>
            )}

            {Object.entries(groupedLasLayers).map(([type, layers]) => (
                <div key={type}>
                    <h6 style={{ marginTop: "10px", fontWeight: "bold" }}>
                        {type.toUpperCase()} slojevi
                    </h6>

                    <Accordion alwaysOpen>
                        {layers.map((layer, index) => (
                            <Accordion.Item
                                eventKey={`${type}-${index}`}
                                key={layer.id || index}
                            >
                                <Accordion.Header>
                                    {formatLayerDisplayName(layer)}
                                </Accordion.Header>

                                <Accordion.Body>
                                    {renderVisibilityControl({
                                        layer,
                                        onToggle: () =>
                                            toggleVisibility(
                                                layer,
                                                index,
                                                type,
                                                false,
                                                true
                                            ),
                                    })}

                                    {renderOpacityControl({
                                        layer,
                                        onChange: (e) =>
                                            changeOpacity(
                                                layer,
                                                e.target.value,
                                                index,
                                                type,
                                                false,
                                                true
                                            ),
                                    })}

                                    {renderGroupControl(layer, "las")}

                                    <LasLayerControls
                                        layer={layer}
                                        index={index}
                                        lasLayers={lasLayers}
                                        setLasLayers={setLasLayers}
                                    />

                                    {renderDeleteButton({
                                        onDelete: () =>
                                            deleteLayer(
                                                layer,
                                                index,
                                                type,
                                                false,
                                                true
                                            ),
                                    })}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </div>
            ))}

            <LayerGroupsManager
                groups={groups}
                setGroups={setGroups}
                layers={[...wfsLayers, ...wmsLayers, ...lasLayers]}
                setLayers={(newLayers) => {
                    setWfsLayers(
                        newLayers.filter((l) =>
                            wfsLayers.some((w) => w.id === l.id)
                        )
                    );

                    setWmsLayers(
                        newLayers.filter((l) =>
                            wmsLayers.some((w) => w.id === l.id)
                        )
                    );

                    setLasLayers(
                        newLayers.filter((l) =>
                            lasLayers.some((las) => las.id === l.id)
                        )
                    );
                }}
                toggleVisibility={toggleVisibility}
                changeOpacity={changeOpacity}
                deleteLayer={deleteLayer}
                moveLayer={moveLayer}
                removeLayerFromGroups={removeLayerFromGroups}
                wfsLayers={wfsLayers}
                wmsLayers={wmsLayers}
                lasLayers={lasLayers}
            />
        </div>
    );
};

export default LayerPanel;